import React, { useState } from "react";
import { Trash2, Edit, ExternalLink } from "lucide-react";
import { API_URLS } from "../../App";
import { CmsPage } from "../../lib/cms/types";
import { extractApiError } from "../../helpers/apiErrors";
import api from "../../hooks/api";

interface CmsPagesListProps {
  pages: CmsPage[];
  loading: boolean;
  onEdit: (page?: CmsPage) => void;
  onDelete: (page: CmsPage) => void;
  setError: (error: string | null) => void;
}

const SITE_BASE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://new.eurosys.pt";

function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CmsPagesList: React.FC<CmsPagesListProps> = ({
  pages,
  loading,
  onEdit,
  onDelete,
  setError,
}) => {
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);
  const [confirmSlug, setConfirmSlug] = useState<string | null>(null);

  const handleDelete = async (page: CmsPage) => {
    setConfirmSlug(null);
    setDeletingSlug(page.slug);
    try {
      await api.delete(`${API_URLS.CMS_ADMIN}/${page.slug}`);
      onDelete(page);
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao apagar página."));
    } finally {
      setDeletingSlug(null);
    }
  };

  const previewUrl = (slug: string) =>
    slug === "home" ? SITE_BASE_URL : `${SITE_BASE_URL}/${slug}`;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <p className="mt-4 text-gray-600">A carregar páginas...</p>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <p className="text-gray-500">Nenhuma página encontrada.</p>
        <p className="text-sm text-gray-400 mt-2">
          Clique em "+ Nova Página" para criar a primeira.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Slug
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Título
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              SEO Title
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Secções
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Atualizada
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {pages.map((page) => (
            <tr key={page.slug} className="hover:bg-gray-50 transition">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                {page.slug}
              </td>
              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                {page.title || (
                  <span className="text-gray-400 italic">Sem título</span>
                )}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {page.seo_title || (
                  <span className="text-gray-400 italic">—</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {Array.isArray(page.sections) ? page.sections.length : 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(page.updated_at)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex gap-2 justify-end items-center">
                  <a
                    href={previewUrl(page.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded transition"
                    title="Ver no site"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => onEdit(page)}
                    className="text-blue-600 hover:text-blue-900 p-1.5 hover:bg-blue-50 rounded transition"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  {confirmSlug === page.slug ? (
                    <span className="flex items-center gap-1.5 text-xs bg-red-50 px-2 py-1 rounded-md whitespace-nowrap">
                      <span className="text-red-600">Apagar?</span>
                      <button
                        onClick={() => handleDelete(page)}
                        disabled={deletingSlug === page.slug}
                        className="text-red-700 font-medium hover:underline disabled:opacity-50"
                      >
                        Sim
                      </button>
                      <button
                        onClick={() => setConfirmSlug(null)}
                        className="text-gray-500 hover:underline"
                      >
                        Não
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmSlug(page.slug)}
                      disabled={deletingSlug !== null}
                      className="text-red-600 hover:text-red-900 p-1.5 hover:bg-red-50 rounded transition disabled:opacity-50"
                      title="Apagar"
                    >
                      {deletingSlug === page.slug ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CmsPagesList;
